import React, { useState, useEffect } from 'react';
import { learningHooksApi } from '../../services/api';

interface LearningHook {
  id: number;
  title: string;
  description: string;
  grade_levels: string;
  project_type: string;
  max_students: number;
  enrolled_count: number;
  is_active: boolean;
}

interface ImportTemplate {
  required_columns: Array<{
    name: string;
    description: string;
    example: string;
  }>;
  sample_data: Array<{
    first_name: string;
    last_name: string;
    email: string;
    grade_level: string;
    student_id: string;
  }>;
  file_formats: Array<{
    extension: string;
    description: string;
  }>;
}

interface ImportResults {
  total_processed: number;
  created: number;
  updated: number;
  enrolled: number;
  errors: string[];
}

export const StudentImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [selectedHook, setSelectedHook] = useState<number | null>(null);
  const [hooks, setHooks] = useState<LearningHook[]>([]);
  const [template, setTemplate] = useState<ImportTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const hooksResponse = await learningHooksApi.getAll();
      // Handle the response structure: {"learning_hooks": [...]}
      const hooksData = hooksResponse.data.learning_hooks || hooksResponse.data.results || hooksResponse.data || [];
      setHooks(Array.isArray(hooksData) ? hooksData : []);
      
      // Set default template
      setTemplate({
        required_columns: [
          { name: 'first_name', description: 'Student first name', example: 'John' },
          { name: 'last_name', description: 'Student last name', example: 'Smith' },
          { name: 'email', description: 'Student email address', example: 'john.smith@students.elevate208.org' },
          { name: 'grade_level', description: 'Grade level (6th, 7th, 8th, 9th, 10th, 11th, 12th)', example: '6th' },
          { name: 'student_id', description: 'Optional student ID from school system', example: '12345' }
        ],
        sample_data: [
          { first_name: 'John', last_name: 'Smith', email: 'john.smith@students.elevate208.org', grade_level: '6th', student_id: '12345' }
        ],
        file_formats: [
          { extension: '.csv', description: 'Comma Separated Values file' },
          { extension: '.xlsx', description: 'Microsoft Excel file' },
          { extension: '.xls', description: 'Legacy Excel file' }
        ]
      });
      setError('');
    } catch (err: any) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.toLowerCase().split('.').pop();
      if (['csv', 'xlsx', 'xls'].includes(fileExtension || '')) {
        setFile(selectedFile);
        setError('');
      } else {
        setError('Please select a CSV (.csv) or Excel (.xlsx, .xls) file');
        setFile(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    if (!selectedHook) {
      setError('Please select a learning hook');
      return;
    }

    setImporting(true);
    setError('');
    setResults(null);

    try {
      const response = await learningHooksApi.importStudents(file, selectedHook);
      setResults(response.data.results);
      
      // Reset form on success
      setFile(null);
      setSelectedHook(null);
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      // Refresh hooks to update student counts
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    if (!template) return;

    // Create CSV content
    const headers = template.required_columns.map(col => col.name).join(',');
    const sampleRow = template.sample_data[0];
    const sampleData = `${sampleRow.first_name},${sampleRow.last_name},${sampleRow.email},${sampleRow.grade_level},${sampleRow.student_id}`;
    
    const csvContent = `${headers}\n${sampleData}\n\n# Instructions:\n# 1. Replace sample data with your student data\n# 2. Required columns: first_name, last_name, email, grade_level\n# 3. Optional column: student_id\n# 4. Grade levels must be: 6th, 7th, 8th, 9th, 10th, 11th, 12th\n# 5. Email addresses must be valid\n# 6. Save as CSV file (.csv) or Excel file (.xlsx)`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-6">Import Students</h2>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {results && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-6">
          <h3 className="font-bold mb-2">Import Complete!</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Total Processed:</strong> {results.total_processed}
            </div>
            <div>
              <strong>Created:</strong> {results.created}
            </div>
            <div>
              <strong>Updated:</strong> {results.updated}
            </div>
            <div>
              <strong>Enrolled:</strong> {results.enrolled}
            </div>
          </div>
          {results.errors.length > 0 && (
            <div className="mt-3">
              <strong className="text-red-600">Errors ({results.errors.length}):</strong>
              <ul className="mt-1 text-sm">
                {results.errors.slice(0, 5).map((error, index) => (
                  <li key={index} className="text-red-600">• {error}</li>
                ))}
                {results.errors.length > 5 && (
                  <li className="text-red-600">• ... and {results.errors.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Learning Hook Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Learning Hook *
          </label>
          <select
            value={selectedHook || ''}
            onChange={(e) => setSelectedHook(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Choose a learning hook...</option>
            {hooks.map(hook => (
              <option key={hook.id} value={hook.id}>
                {hook.title} ({hook.grade_levels}) - {hook.enrolled_count} enrolled
              </option>
            ))}
          </select>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload File *
          </label>
          <input
            id="file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-sm text-gray-500 mt-1">
            Supports CSV (.csv) and Excel (.xlsx, .xls) files
          </p>
        </div>
      </div>

      {/* Template Download */}
      {template && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="font-bold mb-3">File Format Requirements:</h3>
          
          <div className="mb-4">
            <strong>Required Columns:</strong>
            <ul className="mt-1 text-sm space-y-1">
              {template.required_columns.map(col => (
                <li key={col.name} className="flex justify-between">
                  <span><code>{col.name}</code> - {col.description}</span>
                  <span className="text-gray-500">e.g., "{col.example}"</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-4">
            <strong>Supported File Formats:</strong>
            <ul className="mt-1 text-sm">
              {template.file_formats.map(format => (
                <li key={format.extension}>• {format.extension} - {format.description}</li>
              ))}
            </ul>
          </div>

          <button
            onClick={downloadTemplate}
            className="bg-blue-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-600"
          >
            📥 Download Template CSV
          </button>
        </div>
      )}

      {/* Import Button */}
      <div className="flex justify-end">
        <button
          onClick={handleImport}
          disabled={!file || !selectedHook || importing}
          className="bg-green-500 text-white px-6 py-3 rounded font-bold hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {importing ? 'Importing...' : '📥 Import Students'}
        </button>
      </div>
    </div>
  );
};
